import { FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiGet, apiGetPublic, apiPost, ApiError } from "../lib/api";
import { signUp, confirmSignUp, resendConfirmationCode, signIn } from "../lib/auth";
import { PasswordField } from "../components/PasswordField";
import { ConsentForm } from "../components/ConsentForm";
import { unmetPasswordRules } from "../lib/passwordRules";
import { useQuery } from "@tanstack/react-query";

interface InviteInfo {
  status?: "pending" | "accepted";
  inviteType: "minor" | "adult";
  contactName: string;
  /**
   * The address the invite was sent to. Pre-filled and locked below rather than retyped: a typo here
   * used to create a Cognito account whose confirmation code went somewhere unreachable, with no way
   * back from the confirm step (UX review F9).
   */
  contactEmail: string;
  athleteName: string;
  coachName: string | null;
}

type Step = "loading" | "invalid" | "already-accepted" | "welcome" | "account" | "confirm" | "consent" | "billing";

/** Why a token didn't resolve, so the dead-end screen can say which of them happened (UX review F85). */
type InvalidReason = "cancelled" | "expired" | "unknown" | "offline";

/**
 * The steps a person actually walks, for "Step 2 of 5".
 *
 * Signup used to give no sense of its own length -- no counter, no progress, no back link -- so the
 * decision to start was made on an estimate the invitee had no way to form (UX review F13).
 */
/** One of the coach's subscription tiers, as shown on the billing step. */
interface CoachTier {
  id: string;
  name: string;
  priceCents: number;
  description: string | null;
}

const STEPS: Step[] = ["welcome", "account", "confirm", "consent", "billing"];

/** The invitee's side of signup (PRD §4.2): Welcome -> Account -> Confirm -> Consent -> Billing (redirects to Stripe Checkout). */
export function Signup() {
  const { token } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [step, setStep] = useState<Step>("loading");

  /**
   * The coach's plan catalogue, so the billing step can show prices before asking for a card (F12).
   *
   * `enabled` on the billing step alone: this is a signed-in read (the account exists by then, since
   * consent has already been recorded), and firing it while somebody is still typing their password
   * would 401 for no reason.
   */
  const plans = useQuery<{ plans: CoachTier[] }>({
    queryKey: ["billing", "plans"],
    queryFn: () => apiGet<{ plans: CoachTier[] }>("/billing/plans"),
    enabled: step === "billing",
  });
  /**
   * The plan the coach set this family up on (27 Sep debug notes item 12). The family no longer picks
   * at signup -- checkout bills what the coach chose -- so this step names it rather than offering the
   * whole catalogue as if it were a choice.
   */
  const summary = useQuery<{ athletes?: { plan_id: string | null }[] }>({
    queryKey: ["billing", "family", "me"],
    queryFn: () => apiGet<{ athletes?: { plan_id: string | null }[] }>("/billing/family-summary"),
    enabled: step === "billing",
  });
  const chosenPlanIds = new Set(
    (summary.data?.athletes ?? []).map((athlete) => athlete.plan_id).filter((id): id is string => Boolean(id))
  );
  const shownPlans = (plans.data?.plans ?? []).filter((plan) => chosenPlanIds.size === 0 || chosenPlanIds.has(plan.id));
  const [invalidReason, setInvalidReason] = useState<InvalidReason>("unknown");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  /**
   * The opaque username Cognito actually created, kept from signUp's result.
   *
   * Not cosmetic bookkeeping: email is an alias on this pool and an alias does not resolve to an
   * unconfirmed user, so confirming and resending MUST address the account by this username. Using the
   * email made every confirmation fail (see confirmSignUp in lib/auth.ts).
   */
  const [cognitoUsername, setCognitoUsername] = useState("");
  /** Feedback for "Resend code", which previously did its work with no visible sign it had happened. */
  const [resendNotice, setResendNotice] = useState<string | null>(null);


  useEffect(() => {
    if (!token) return;
    /**
     * `apiGetPublic`, not `apiGet`. This is the one endpoint a brand-new customer can reach, and they
     * have no session by definition -- the authenticated client refused the call before it was made and
     * rendered every real invite link as "Invite not found" (UX review F60).
     */
    apiGetPublic<InviteInfo>(`/invites/by-token/${token}`)
      .then((info) => {
        if (info.status === "accepted") {
          setStep("already-accepted");
          return;
        }
        setInvite(info);
        setEmail(info.contactEmail ?? "");
        setStep("welcome");
      })
      .catch((err: unknown) => {
        // "no such invite" and "your phone has no signal" are not the same thing, and telling someone to
        // ask their coach for a new link when they are simply offline sends them somewhere useless.
        const reason =
          err instanceof ApiError && err.status === 0
            ? "offline"
            : ((err as ApiError)?.details?.reason as InvalidReason) ?? "unknown";
        setInvalidReason(reason);
        setStep("invalid");
      });
  }, [token]);

  async function handleCreateAccount(event: FormEvent) {
    event.preventDefault();
    // Caught before Cognito, so a mistyped password is a correction rather than an account created
    // with a password the person cannot reproduce -- which, with confirmation in between, they would
    // only discover at the sign-in step.
    if (password !== confirmPassword) {
      setError("Those passwords don't match.");
      return;
    }
    // Checked here as well as by the field's own hints so that submitting can never hand Cognito a
    // password it will reject with its own internal wording (UX review F7).
    const unmet = unmetPasswordRules(password);
    if (unmet.length > 0) {
      setError(`Your password still needs: ${unmet.map((rule) => rule.label).join(", ")}.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await signUp({ name, email, password, role: "family", inviteToken: token });
      setCognitoUsername(result.user.getUsername());
      setStep("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmCode(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await confirmSignUp(cognitoUsername, confirmationCode);
      // Signing in by username too: the email alias only becomes usable once the account is confirmed,
      // and this runs immediately after. Later sign-ins from the login page use the email as normal.
      await signIn(cognitoUsername, password);
      setStep("consent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to confirm your account");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    setError(null);
    setResendNotice(null);
    try {
      await resendConfirmationCode(cognitoUsername);
      setResendNotice(`Sent again to ${email}. It can take a minute to arrive.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend the confirmation code");
    }
  }

  const parentalConsentRequired = invite?.inviteType === "minor";

  async function handleConsent(consent: { privacyPolicyAccepted: true; tosAccepted: true; parentalConsentAccepted?: true }) {
    setSubmitting(true);
    setError(null);
    try {
      await apiPost("/consent", consent);
      setStep("billing");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record consent");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoToBilling() {
    setSubmitting(true);
    setError(null);
    try {
      const { url } = await apiPost<{ url: string }>("/billing/checkout-session");
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start billing setup");
      setSubmitting(false);
    }
  }

  if (step === "loading") {
    return (
      <div className="centered-page">
        <p>Loading…</p>
      </div>
    );
  }

  if (step === "already-accepted") {
    return (
      <div className="centered-page">
        <div className="card signup-card">
          <h1>You're already set up</h1>
          <p>
            This invite has already been used, which means the account exists. Sign in with the email
            address it was sent to.
          </p>
          <Link className="link-button" to="/signin">
            Go to sign in
          </Link>
          <p className="form-hint">
            Forgotten the password? Use <Link to="/forgot-password">Forgot your password</Link> on the
            sign-in screen — your coach can't reset it for you.
          </p>
        </div>
      </div>
    );
  }

  if (step === "invalid") {
    return (
      <div className="centered-page">
        <div className="card signup-card">
          <h1>{invalidReason === "offline" ? "Couldn't load your invite" : "This invite link doesn't work"}</h1>
          {/* One screen used to answer for accepted, cancelled, expired, unknown and offline alike,
              and it told all five to ask the coach for a new link (UX review F85). */}
          <p>{INVALID_COPY[invalidReason]}</p>
          {invalidReason === "offline" ? (
            <button type="button" onClick={() => window.location.reload()}>
              Try again
            </button>
          ) : (
            <p className="form-hint">
              Already set one up? <Link to="/signin">Sign in instead</Link>.
            </p>
          )}
        </div>
      </div>
    );
  }

  const stepNumber = STEPS.indexOf(step) + 1;

  return (
    <div className="centered-page">
      <div className="card signup-card">
        {stepNumber > 0 && (
          <p className="signup-progress" aria-label={`Step ${stepNumber} of ${STEPS.length}`}>
            Step {stepNumber} of {STEPS.length}
          </p>
        )}

        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}

        {step === "welcome" && invite && (
          <>
            <h1>{invite.coachName ? `${invite.coachName} has invited you` : "Welcome"}</h1>
            {/* Written as two whole sentences rather than one with an optional clause. The clause was
                empty for an adult invite, which left "invited by your coach ." with a dangling space
                before the full stop -- the kind of thing that reads as a broken page on the very first
                screen a new family sees. */}
            <p>
              {invite.inviteType === "minor"
                ? `You've been invited to set up a family account for ${invite.athleteName}.`
                : "You've been invited to join Athlete’s Advantage."}{" "}
              {invite.coachName
                ? `It's the off-ice training app ${invite.coachName} uses with the skaters they coach.`
                : "It's the off-ice training app your coach uses with the skaters they coach."}
            </p>
            {/* Consent and a card arrive several screens in. Being told they're coming is not a
                deterrent; discovering them halfway through, at a rink, is (UX review F3). */}
            <p className="form-hint">
              It takes about five minutes. You'll create a password, agree to a couple of consents, and
              choose a plan and add a payment method at the end.
            </p>
            <button onClick={() => setStep("account")}>Get started</button>
          </>
        )}

        {step === "account" && invite && (
          <form onSubmit={handleCreateAccount}>
            <h1>Create your account</h1>
            {/* Says whose name, because on a minor invite it is not the skater's.
                The account being created belongs to the adult, and the athlete's name was set by the
                coach on the invite -- but "Your name", on a page that has just been talking about a
                child, reads as a prompt for the child's. Somebody typing their nine-year-old's name
                here ends up with an account addressed to a minor and a guardian who cannot be
                identified on their own billing. */}
            <label htmlFor="signup-name">
              {invite.inviteType === "minor" ? "Your name (the parent or guardian)" : "Your name"}
            </label>
            <input
              id="signup-name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <label htmlFor="signup-email">Email</label>
            {/* Read-only rather than absent: the address is the identity the invite was issued to, and
                seeing it is what tells someone the link is theirs. */}
            <input id="signup-email" type="email" autoComplete="email" value={email} readOnly required />
            <p className="form-hint">
              Your invite was sent to this address, so it's the one your account uses.
            </p>

            <PasswordField
              id="signup-password"
              label="Password"
              autoComplete="new-password"
              value={password}
              onChange={setPassword}
              showRules
            />
            <PasswordField
              id="signup-confirm-password"
              label="Confirm password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={setConfirmPassword}
            />

            <button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Continue"}
            </button>
          </form>
        )}

        {step === "confirm" && (
          <form onSubmit={handleConfirmCode}>
            <h1>Check your email</h1>
            <p>We sent a confirmation code to {email}.</p>
            <label htmlFor="signup-code">Confirmation code</label>
            <input
              id="signup-code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              required
              inputMode="numeric"
              autoComplete="one-time-code"
            />

            <button type="submit" disabled={submitting}>
              {submitting ? "Confirming…" : "Confirm"}
            </button>
            <button type="button" className="chip" onClick={handleResendCode}>
              Resend code
            </button>
            {resendNotice && <p className="form-hint">{resendNotice}</p>}
            {/* The way out of a dead end. Before this, an address that couldn't receive mail left the
                account unreachable with only a resend to the same place (UX review F9). */}
            <button type="button" className="chip" onClick={() => setStep("account")}>
              Back
            </button>
          </form>
        )}

        {step === "consent" && (
          <>
            <h1>Consent</h1>
            <ConsentForm
              athleteName={parentalConsentRequired && invite ? invite.athleteName : null}
              submitting={submitting}
              onSubmit={handleConsent}
            />
          </>
        )}

        {step === "billing" && (
          <>
            <h1>Your plan</h1>
            {/* The prices, here, before a card is asked for.
                This screen said only "you'll pick a plan and add a card on the next screen" -- so the
                last step of signing up asked for payment details without ever having shown a number,
                which is where somebody handing money to a brand they met yesterday decides whether to
                carry on (UX review F12). The tiers are the coach's own, read from the same catalogue
                the family will see afterwards. */}
            <p>
              Your coach has set you up on this plan. You&rsquo;ll add a card or bank account (ACH) on the
              next screen &mdash; nothing is charged until you do. Once you&rsquo;re signed up, you can talk
              to your coach about switching.
            </p>
            {/* `data?.plans?.` rather than trusting the shape: this is the last screen before somebody
                hands over a card, and an unexpected response must degrade to "no prices shown" rather
                than replacing the page with an error boundary. */}
            {shownPlans.length ? (
              <ul className="plan-catalogue signup-plans">
                {shownPlans.map((plan) => (
                  <li key={plan.id} className="plan-tile">
                    <div className="plan-tile-head">
                      <span className="client-name">{plan.name}</span>
                      <span className="badge">${(plan.priceCents / 100).toFixed(0)}/mo</span>
                    </div>
                    {plan.description && <p className="plan-description">{plan.description}</p>}
                  </li>
                ))}
              </ul>
            ) : null}
            <button onClick={handleGoToBilling} disabled={submitting}>
              {submitting ? "Redirecting…" : "Continue"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const INVALID_COPY: Record<InvalidReason, string> = {
  cancelled: "Your coach withdrew this invitation. If that's a surprise, ask them to send a new one.",
  expired: "Invitations expire after a couple of weeks. Ask your coach to send a fresh one.",
  unknown: "We couldn't find an invitation for this link. Check you've opened the most recent email — a resent invite replaces the previous link.",
  offline: "We couldn't reach the server. Check your connection and try again — the link itself is probably fine.",
};
